import {
  siPinterest, siReddit, siMeta, siFacebook, siInstagram, siGoogleads,
  siGoogleanalytics, siTiktok, siSnapchat, siYoutube, siX, siWoocommerce,
} from "simple-icons";
import { BRAND_SVG, BRAND_IMG } from "@/components/ui/brand-svg";
import { cn } from "@/lib/utils";

/** Brand tile colors for platforms rendered as an accent monogram (no icon-set mark). */
const MONO_COLOR: Record<string, string> = {};

type Glyph = { path: string; hex: string };

/** simple-icons that ship the brand exactly. */
const SI: Record<string, Glyph> = {
  pinterest: siPinterest,
  reddit: siReddit,
  meta: siMeta,
  facebook: siFacebook,
  instagram: siInstagram,
  google_ads: siGoogleads,
  googleads: siGoogleads,
  google: siGoogleads,
  ga4: siGoogleanalytics,
  woocommerce: siWoocommerce,
  google_analytics: siGoogleanalytics,
  googleanalytics: siGoogleanalytics,
  tiktok: siTiktok,
  snapchat: siSnapchat,
  youtube: siYoutube,
  x: siX,
  twitter: siX,
};

/** Brands simple-icons no longer ships (removed on request) — hand-authored. */
const LOCAL: Record<string, Glyph> = {
  linkedin: {
    hex: "0A66C2",
    path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
  },
};

/** Aliases → canonical logo key. */
const ALIAS: Record<string, string> = {
  microsoft: "bing",
  bing: "bing", // rendered as monogram (no clean single-path mark)
  klaviyo: "klaviyo",
  teamtailor: "teamtailor",
};

function relLuminance(hex: string): number {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Brand color for a platform's bar/dot — the real brand hex where known,
 *  else the platform's accent_color. Pure black marks are darkened to a
 *  neutral so bars stay visible. */
const BRAND_HEX: Record<string, string> = { klaviyo: "#232426", teamtailor: "#F43F85", bing: "#0C8484", microsoft: "#0C8484" };
export function platformBrandColor(platform: string, accentColor?: string): string {
  const key = platform?.toLowerCase().replace(/[\s-]/g, "_") ?? "";
  if (BRAND_HEX[key]) return BRAND_HEX[key];
  const g = SI[key] ?? LOCAL[key];
  if (g) return relLuminance(g.hex) < 0.06 ? "#111827" : `#${g.hex}`;
  return accentColor ?? "#64748b";
}

export interface PlatformLogoProps {
  /** platform key from the DB (klaviyo, pinterest, meta, …) */
  platform: string;
  /** fallback tile color when no brand mark exists (the platform's accent_color) */
  accentColor?: string;
  size?: number;
  className?: string;
  /** render inside a rounded tinted tile (default) vs a bare glyph */
  tile?: boolean;
}

/** Brand logo for a platform — real mark where one exists, else an accent
 *  monogram tile. All SVG is inlined (CSP-safe, no external requests). */
export function PlatformLogo({ platform, accentColor, size = 20, className, tile = true }: PlatformLogoProps) {
  const key = platform?.toLowerCase().replace(/[\s-]/g, "_") ?? "";

  // Real raster brand logos (Klaviyo) embedded as data URIs.
  const img = BRAND_IMG[key];
  if (img) {
    const el = <img src={img} alt={platform} width={tile ? size : size} height={tile ? size : size} className="object-contain" draggable={false} />;
    if (!tile) return <span className={cn("inline-flex", className)} style={{ width: size, height: size }}>{el}</span>;
    return (
      <span
        className={cn("inline-flex items-center justify-center overflow-hidden rounded-[7px] bg-white ring-1 ring-black/5 dark:ring-white/10", className)}
        style={{ width: size + 12, height: size + 12 }}
      >
        {el}
      </span>
    );
  }

  // Real full-SVG brand icons (Teamtailor, Bing) inlined from brand-svg.ts.
  const raw = BRAND_SVG[key];
  if (raw) {
    if (raw.selfTile) {
      const box = tile ? size + 12 : size;
      return (
        <span
          className={cn("inline-flex overflow-hidden rounded-[7px] [&>svg]:h-full [&>svg]:w-full", className)}
          style={{ width: box, height: box }}
          dangerouslySetInnerHTML={{ __html: raw.svg }}
        />
      );
    }
    const glyphEl = (
      <span
        className="inline-flex items-center justify-center [&>svg]:h-full [&>svg]:w-auto"
        style={{ height: size }}
        dangerouslySetInnerHTML={{ __html: raw.svg }}
      />
    );
    if (!tile) return <span className={className}>{glyphEl}</span>;
    return (
      <span
        className={cn("inline-flex items-center justify-center rounded-[7px] bg-white ring-1 ring-black/5 dark:ring-white/10", className)}
        style={{ width: size + 12, height: size + 12 }}
      >
        {glyphEl}
      </span>
    );
  }

  const glyph = SI[key] ?? LOCAL[key];

  if (glyph) {
    // Keep near-black / near-white marks visible on either theme.
    const lum = relLuminance(glyph.hex);
    const fill = lum < 0.08 || lum > 0.92 ? "var(--foreground)" : `#${glyph.hex}`;
    const glyphEl = (
      <svg viewBox="0 0 24 24" width={size} height={size} role="img" aria-label={platform} fill={fill}>
        <path d={glyph.path} />
      </svg>
    );
    if (!tile) return <span className={className}>{glyphEl}</span>;
    return (
      <span
        className={cn("inline-flex items-center justify-center rounded-[7px] ring-1 ring-black/5 dark:ring-white/10", className)}
        style={{ width: size + 12, height: size + 12, background: `#${glyph.hex}14` }}
      >
        {glyphEl}
      </span>
    );
  }

  // Monogram fallback (Klaviyo / unknown) — brand tile colour where known.
  const label = (ALIAS[key] ?? platform ?? "?").charAt(0).toUpperCase();
  const bg = MONO_COLOR[key] ?? accentColor ?? "#64748b";
  if (!tile) {
    return <span className={cn("font-heading font-semibold", className)} style={{ color: bg, fontSize: size * 0.9 }}>{label}</span>;
  }
  return (
    <span
      className={cn("inline-flex items-center justify-center rounded-[7px] font-heading font-semibold text-white", className)}
      style={{ width: size + 12, height: size + 12, background: bg, fontSize: size * 0.62 }}
    >
      {label}
    </span>
  );
}
